import { useRef } from "react"
import { Link } from "react-router-dom"
import { ExternalLink } from "lucide-react"
import { DateTime } from "luxon"
import { CellContext, PaginationState } from "@tanstack/react-table"
import Table from "../../table"
import DateFilter, {
  DateFilterHandle,
  DateRangePreset
} from "../../activity/date-filter"
import { BBColumnDef, Thread } from "../../../../types"
import Badges from "../../conversations-list-item/badges"

const formatDate = (iso: string) =>
  DateTime.fromISO(iso).toFormat("MMM dd, yyyy · h:mm a")

interface Props {
  threads: Thread[]
  isLoading: boolean
  totalRows: number
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  pageCount: number
  searchQuery: string
  onSearchChange: (value: string) => void
  datePreset: DateRangePreset
  customDateStart: number | null
  customDateEnd: number | null
  onDateChange: (
    range: DateRangePreset,
    startDate?: Date,
    endDate?: Date
  ) => void
  onClearDate: () => void
}

const SmsThreadsTable = ({
  threads,
  isLoading,
  totalRows,
  pagination,
  setPagination,
  pageCount,
  searchQuery,
  onSearchChange,
  datePreset,
  customDateStart,
  customDateEnd,
  onDateChange,
  onClearDate
}: Props) => {
  const dateFilterRef = useRef<DateFilterHandle>(null)

  const columns: BBColumnDef<Thread>[] = [
    {
      id: "leadName",
      header: "Lead Name",
      accessorFn: row =>
        `${row.first_name ?? ""} ${row.last_name ?? ""} ${row.email ?? ""} ${
          row.phone ?? ""
        }`,
      cell: ({ row }: CellContext<Thread, unknown>) => {
        const t = row.original
        const name = `${t.first_name ?? ""} ${t.last_name ?? ""}`.trim()
        return (
          <td className="py-4 pl-2 pr-3">
            <div className="font-semibold text-sm">{name || "—"}</div>
            {t.email && (
              <div className="text-xs text-neutral-400 truncate max-w-xs">
                {t.email}
              </div>
            )}
            {t.phone && (
              <div className="text-xs text-neutral-400">{t.phone}</div>
            )}
            <Badges thread={t} />
          </td>
        )
      }
    },
    {
      id: "lastActivity",
      header: "Last Activity",
      accessorFn: row => row.updatedAt,
      cell: ({ row }: CellContext<Thread, unknown>) => (
        <td className="py-4 pr-3 text-sm text-neutral-500 whitespace-nowrap">
          {row.original.updatedAt ? formatDate(row.original.updatedAt) : "—"}
        </td>
      ),
      meta: { headerClassName: "hidden md:table-cell" },
      enableSorting: true
    },
    {
      id: "lastMessage",
      header: "Last Message",
      enableSorting: false,
      accessorFn: row => row.messages?.at(-1)?.message ?? "",
      cell: ({ row }: CellContext<Thread, unknown>) => {
        const last = row.original.messages?.at(-1)
        const isSystem = last?.sender === "system"
        return (
          <td className="py-4 pr-3 max-w-xs">
            {last ? (
              <>
                <div className="text-sm text-neutral-700 truncate">
                  {last.message}
                </div>
                <div className="text-xs text-neutral-400 mt-0.5">
                  {isSystem ? "You" : row.original.first_name || "Lead"}
                </div>
              </>
            ) : (
              <span className="text-sm text-neutral-300">—</span>
            )}
          </td>
        )
      }
    },
    {
      id: "action",
      header: "",
      enableSorting: false,
      meta: { headerClassName: "w-6 sr-only" },
      cell: ({ row }: CellContext<Thread, unknown>) => (
        <td className="px-5 py-3 text-right">
          <Link
            target="_blank"
            to={`/conversations/sms/${row.original._id}`}
            className="inline-flex text-neutral-400 hover:text-blue-400 transition-colors"
            title="Open in Conversations"
          >
            <ExternalLink className="w-4 h-4" />
          </Link>
        </td>
      )
    }
  ]

  return (
    <Table<Thread>
      isLoading={isLoading}
      data={threads}
      columns={columns}
      viewType="table"
      searchPlaceholder="Search by name or email…"
      pagination={pagination}
      setPagination={setPagination}
      pageCount={pageCount}
      rowCount={totalRows}
      onSearchChange={onSearchChange}
      pageSize={pagination.pageSize}
      extraHeaderElement={
        <DateFilter
          ref={dateFilterRef}
          selectedRange={datePreset}
          customStartDate={customDateStart ? new Date(customDateStart) : null}
          customEndDate={customDateEnd ? new Date(customDateEnd) : null}
          onRangeChange={onDateChange}
          onClearFilter={onClearDate}
        />
      }
      extraHeaderElementClassName="ml-auto"
      shrinkFilterSort
      defaultSortBy="lastActivity"
      defaultSortDir="desc"
      noSorter={false}
    />
  )
}

export default SmsThreadsTable
